import moment from 'moment';

const relativeDateFormat = new Intl.RelativeTimeFormat('ru', {
	numeric: 'auto',
	style: 'long'
});

const timeFormat = new Intl.DateTimeFormat('ru', {
	hour: 'numeric',
	minute: 'numeric',
});

export const timeFullFormat = new Intl.DateTimeFormat('ru', {
	hour: 'numeric',
	minute: 'numeric',
	second: 'numeric',
});

const dateFormat = new Intl.DateTimeFormat('ru');

export function getRelativeDateString(date: Date, today: Date): string {
	const
		diffTimeStart = date.getTime() - today.getTime(),
		diffTimeStartInDays = diffTimeStart / (1000 * 3600 * 24);

	return `${relativeDateFormat.format(diffTimeStartInDays, 'day')}`;
}

export function getDateString(date: moment.MomentInput): string {
	return moment(date).format("DD.MM.YYYY");
}

export function getDateTimeString(date: moment.MomentInput): string {
	return moment(date).format("DD.MM.YYYY HH:mm:ss");
}

export function makeMeetingDateString(start: Date, end: Date): string {
	start = new Date(start);
	end = new Date(end);
	const startD = new Date(start);
	startD.setHours(0, 0, 0, 0);
	const endD = new Date(end);
	endD.setHours(0, 0, 0, 0);
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const startDateDiffDays = moment(today).diff(startD, 'days');
	//console.log({ startD, today, startDateDiffDays });

	let meetingDateFormat = "DD.MM.YYYY";
	if (startD.getFullYear() == today.getFullYear()) {
		meetingDateFormat = "DD MMMM";
	}

	const
		startDate = startDateDiffDays > 1 ? moment(startD).format(meetingDateFormat) : getRelativeDateString(startD, today),
		startTime = timeFormat.format(moment(start).toDate()),
		endDate = startDateDiffDays > 1 ? moment(endD).format(meetingDateFormat) : getRelativeDateString(endD, today),
		endTime = timeFormat.format(moment(end).toDate());

	return startDate == endDate
		? `${startDate}, ${startTime} — ${endTime}`
		: `${startDate}, ${startTime} — ${endDate}, ${endTime}`;
}

export function makeFullDateString(start: Date, end: Date): string {
	start = new Date(start);
	end = new Date(end);
	const startD = new Date(start);
	startD.setHours(0, 0, 0, 0);
	const endD = new Date(end);
	endD.setHours(0, 0, 0, 0);

	const startDate = dateFormat.format(startD),
		startTime = timeFormat.format(start),
		endDate = dateFormat.format(endD),
		endTime = timeFormat.format(end);

	return startDate == endDate
		? `${startDate}, ${startTime} ~ ${endTime}`
		: `${startDate}, ${startTime} ~ ${endDate}, ${endTime}`;
}

export function getDurationDateString(start: Date, end: Date): string {
	const duration = moment.duration(moment(end).diff(start));
	const differenceInMinutes = Math.ceil(duration.asMinutes());

	return `${differenceInMinutes} мин.`;
}

export const test = {
	"Ticks": 67700000,
	"Days": 0,
	"Hours": 0,
	"Milliseconds": 770,
	"Minutes": 0,
	"Seconds": 6,

	"TotalDays": 7.835648148148148E-05,
	"TotalHours": 0.0018805555555555555,
	"TotalMilliseconds": 6770,
	"TotalMinutes": 0.11283333333333333,
	"TotalSeconds": 6.77
};

export interface TimeParts {
	day: number,
	hour: number,
	minute: number,
	second: number,
	milisecond: number,
	totalSeconds: number,
}

export function timespanToParts(ticks: number): TimeParts {
	const
		totalMilliseconds = ticks / 10000,
		totalSeconds = totalMilliseconds / 1000,
		totalMinutes = totalSeconds / 60,
		totalHours = totalMinutes / 60,
		totalDays = totalHours / 24;

	return {
		day: Math.floor(totalDays),
		hour: Math.floor(totalHours % 24),
		minute: Math.floor(totalMinutes % 60),
		second: Math.floor(totalSeconds % 60),
		milisecond: Math.floor(totalMilliseconds % 1000),
		totalSeconds,
	};
}

export const ticksToSeconds = (ticks: number): number => {
	return ticks / 10000000;
}

export const ticksToMilliSeconds = (ticks: number): number => {
	return ticks / 10000;
}

export const secondsToTicks = (seconds: number): number => {
	return seconds * 10000000;
}

export function pad2(numStr: number | string): string {
	numStr = '0' + numStr;
	return numStr.substr(numStr.length - 2);
}

export function makeTime(date: moment.Moment): string {
	const timeString = date.format("HH:mm");
	return timeString;
}

export function makeShortTimespan(ticks: number): string {
	const
		parts = timespanToParts(ticks),
		hours = pad2(parts.hour),
		minutes = pad2(parts.minute),
		seconds = pad2(parts.second);

	return parts.hour ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
}

export function makeFullTimespan(ticks: number): string {
	const parts = timespanToParts(ticks);
	return pad2(parts.hour) + ':' + pad2(parts.minute) + ':' + pad2(parts.second);
}

export function convertSeconds(seconds: number): string {
	const initialDate = new Date(0);
	const date = new Date(0);
	date.setSeconds(isFinite(seconds) ? seconds : 0);

	const diffInHours = moment(date).diff(initialDate, 'hours'),
		showHours = diffInHours >= 1,
		substrArr = showHours ? [11, 8] : [14, 5],
		timeString = date.toISOString().substr(substrArr[0], substrArr[1]);

	return timeString;
}

export const calcTimeElapsed = (recordedDurationMs: number, lastRecordingSpanStartUtc: moment.MomentInput): string => {
	if (!lastRecordingSpanStartUtc) {
		return "00:00";
	}
	const end = new Date();
	let duration = moment.duration(moment(end).diff(lastRecordingSpanStartUtc));
	duration = duration.add(recordedDurationMs, 'milliseconds');
	const hours = duration.hours();
	const minutes = duration.minutes();
	const seconds = duration.seconds();

	return hours ? `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}` : `${pad2(minutes)}:${pad2(seconds)}`;
}

export const convertDurationMs = (recordedDurationMs: number): string => {
	if (!recordedDurationMs) {
		return "00:00";
	}

	const duration = moment.duration(recordedDurationMs, 'milliseconds');
	const hours = duration.hours();
	const minutes = duration.minutes();
	const seconds = duration.seconds();

	return hours ? `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}` : `${pad2(minutes)}:${pad2(seconds)}`;
}

export const calcMeetingRecordTime = (recordedDurationMs: number, lastRecordingSpanStartUtc: moment.MomentInput, isMeetingInPaused: boolean): string => {
	if (!isMeetingInPaused) {
		return calcTimeElapsed(recordedDurationMs, lastRecordingSpanStartUtc);
	} else {
		return convertDurationMs(recordedDurationMs);
	}
}